/**
 * The organisation-level settings governing payroll's "apply merit to payroll" capability
 * (remediation plan §27/§28, decisions D2/P1).
 *
 * `enabled` is the module switch itself (§27): off by default for every organisation, old and new.
 * While off, `payroll-merit.ts`'s routes answer 404 `merit_apply_disabled`; nothing about merit's own
 * recommendation/approval flow changes, and approved proposals simply wait, unconsumed.
 *
 * `netBasisArrearsMode` (§28, decision P1) is what happens when a back-dated increase's arrears
 * would land on a `net` (take-home) pay basis employee. Only two values are implemented:
 *   - `accept_shortfall` (default) — today's only behaviour: the take-home delta is paid as a gross
 *     earning, exactly like any other basis; whatever tax/PF/ESI lands on it is the engine's to
 *     compute, which can leave the employee short of the full take-home difference.
 *   - `block` — refuses the apply instead (`net_basis_arrears_blocked`), nothing written.
 * A third value, `gross_up`, is named in the decision brief but NOT implemented: no net-to-gross
 * calculation exists anywhere in the payroll engine today, and the algorithm needs the payroll
 * reviewers' (Siddhant Yerandkar / Chirag Gehlot) sign-off before it ships, not after.
 *
 * Pure and dependency-free (RN-safe): the contract, the API and the facts loader share it. Validated
 * free text, not a DB enum — deliberately, so adding `gross_up` later needs no migration, following
 * `pay-basis.ts`'s own precedent for an app-level-validated, DB-free-text column.
 */

import { z } from 'zod';

export const NET_BASIS_ARREARS_MODES = ['accept_shortfall', 'block'] as const;
export type NetBasisArrearsMode = (typeof NET_BASIS_ARREARS_MODES)[number];
export const netBasisArrearsModeSchema = z.enum(NET_BASIS_ARREARS_MODES);

/** Today's only built behaviour: the take-home delta is paid as a gross earning, unchanged. */
export const DEFAULT_NET_BASIS_ARREARS_MODE: NetBasisArrearsMode = NET_BASIS_ARREARS_MODES[0];

/**
 * A stored mode. The column has no DB CHECK (see module doc), so an unparseable value — including
 * one written by a future version naming an option this version doesn't know, such as `gross_up`
 * before it ships everywhere — is read as the default rather than trusted, mirroring `payBasisOf`.
 */
export function netBasisArrearsModeOf(stored: string | null | undefined): NetBasisArrearsMode {
  const parsed = netBasisArrearsModeSchema.safeParse(stored);
  return parsed.success ? parsed.data : DEFAULT_NET_BASIS_ARREARS_MODE;
}

/** What a fresh organisation starts with (§27): off, and today's only arrears behaviour. */
export const MERIT_PAYROLL_SETTINGS_DEFAULTS = {
  enabled: false,
  netBasisArrearsMode: DEFAULT_NET_BASIS_ARREARS_MODE,
} as const;
